/**
 * Snake — the pure game engine behind components/SnakeGame.vue.
 *
 * Framework-free on purpose: no DOM, no timers, no randomness of its own (the caller
 * injects `rng`), so it runs under bare `node --test` and every rule here is pinned by
 * tests/unit/snakeGame.test.mjs. The component owns the canvas, the clock and the keys.
 *
 * State is a plain mutable object. Head is `snake[0]`.
 */

export const DEFAULT_COLS = 20;
export const DEFAULT_ROWS = 15;

/**
 * Starting tick length, the floor it speeds up to, and how much each food shaves off.
 * It starts slow on purpose — this is something to do while waiting, not a reflex test —
 * and reaches full speed only after ~28 foods.
 */
export const TICK_START_MS = 250;
export const TICK_MIN_MS = 80;
export const TICK_STEP_MS = 6;

export const DIRS = Object.freeze({
  up: Object.freeze({ x: 0, y: -1 }),
  down: Object.freeze({ x: 0, y: 1 }),
  left: Object.freeze({ x: -1, y: 0 }),
  right: Object.freeze({ x: 1, y: 0 }),
});

export const HIGH_SCORE_KEY = "apiOutageSnakeHighScore";

const START_LENGTH = 3;

function defaultRng() {
  return Math.random();
}

/** Three segments centred on the board, heading right, body trailing left. */
function initialSnake(cols, rows) {
  const headX = Math.floor(cols / 2);
  const y = Math.floor(rows / 2);
  const snake = [];
  for (let i = 0; i < START_LENGTH; i++) snake.push({ x: headX - i, y });
  return snake;
}

/**
 * @param {{ cols?: number, rows?: number, rng?: () => number }} [opts]
 * @returns {{ cols:number, rows:number, snake:{x:number,y:number}[], dir:{x:number,y:number},
 *            nextDir:{x:number,y:number}, food:{x:number,y:number}|null, score:number,
 *            status:"idle"|"playing"|"over" }}
 */
export function createGame({ cols = DEFAULT_COLS, rows = DEFAULT_ROWS, rng = defaultRng } = {}) {
  const state = {
    cols,
    rows,
    snake: initialSnake(cols, rows),
    dir: DIRS.right,
    nextDir: DIRS.right,
    food: null,
    score: 0,
    status: "idle",
  };
  placeFood(state, rng);
  return state;
}

/** Fresh board, status "playing". A no-op mid-run so a stray Space cannot wipe a game. */
export function startGame(state, rng = defaultRng) {
  if (state.status === "playing") return state;
  state.snake = initialSnake(state.cols, state.rows);
  state.dir = DIRS.right;
  state.nextDir = DIRS.right;
  state.score = 0;
  state.status = "playing";
  placeFood(state, rng);
  return state;
}

/**
 * Queue a turn for the next tick. A reversal is judged against the direction the snake
 * is CURRENTLY moving, not the queued one — otherwise "up then left" inside one tick
 * would fold the head straight back into its own neck.
 * @returns {boolean} whether the turn was accepted
 */
export function setDirection(state, name) {
  const dir = DIRS[name];
  if (!dir) return false;
  if (dir.x === -state.dir.x && dir.y === -state.dir.y) return false;
  state.nextDir = dir;
  return true;
}

function isOnSnake(snake, x, y) {
  return snake.some((s) => s.x === x && s.y === y);
}

/**
 * Drop the food on a uniformly chosen free cell. Sets `food = null` when the board is
 * full — the caller treats that as a win.
 */
export function placeFood(state, rng = defaultRng) {
  const free = [];
  for (let y = 0; y < state.rows; y++) {
    for (let x = 0; x < state.cols; x++) {
      if (!isOnSnake(state.snake, x, y)) free.push({ x, y });
    }
  }
  if (free.length === 0) {
    state.food = null;
    return state;
  }
  const idx = Math.min(free.length - 1, Math.floor(rng() * free.length));
  state.food = free[idx];
  return state;
}

/**
 * Advance one tick: move, eat, or die. Only runs while playing.
 * The walls wrap — leaving one side comes back in on the opposite side — so the only way
 * to lose is running into your own body. That hit flips status to "over" WITHOUT adding
 * the offending head, so the last frame drawn is still a legal board.
 */
export function step(state, rng = defaultRng) {
  if (state.status !== "playing") return state;

  state.dir = state.nextDir;
  const head = state.snake[0];
  // (+ cols) % cols keeps a step off the left/top edge positive before wrapping.
  const nx = (head.x + state.dir.x + state.cols) % state.cols;
  const ny = (head.y + state.dir.y + state.rows) % state.rows;

  const eats = !!state.food && state.food.x === nx && state.food.y === ny;
  // The tail cell frees up this tick unless we grow into it, so moving onto it is legal.
  const body = eats ? state.snake : state.snake.slice(0, -1);
  if (isOnSnake(body, nx, ny)) {
    state.status = "over";
    return state;
  }

  state.snake.unshift({ x: nx, y: ny });
  if (eats) {
    state.score += 1;
    placeFood(state, rng);
    if (state.food === null) state.status = "over"; // board full — you win
  } else {
    state.snake.pop();
  }
  return state;
}

/** Milliseconds per tick for a given score — quicker as you eat, never below the floor. */
export function tickIntervalMs(score) {
  const s = Math.max(0, Number(score) || 0);
  return Math.max(TICK_MIN_MS, TICK_START_MS - s * TICK_STEP_MS);
}

/** Best score from a Storage-like object; 0 when absent, corrupt, or the storage throws. */
export function readHighScore(storage) {
  try {
    if (!storage || typeof storage.getItem !== "function") return 0;
    const raw = storage.getItem(HIGH_SCORE_KEY);
    const n = Number.parseInt(raw, 10);
    return Number.isFinite(n) && n > 0 ? n : 0;
  } catch {
    return 0;
  }
}

/**
 * Persist `score` if it beats the stored best. Never throws (private mode, quota).
 * @returns {number} the best after the write
 */
export function writeHighScore(storage, score) {
  const best = readHighScore(storage);
  const s = Math.max(0, Number(score) || 0);
  if (s <= best) return best;
  try {
    if (storage && typeof storage.setItem === "function") storage.setItem(HIGH_SCORE_KEY, String(s));
  } catch {
    /* storage unavailable — the in-memory best still stands for this session */
  }
  return s;
}
